import React, { useEffect, useRef, useState } from 'react';
import { SocialIcons } from '@apps/config/icons.config';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import classNames from 'classnames';
import Link from 'next/link';

import { ContentBody } from '../Content/styles';
import StyledImage from '../Image';
import SocialIconButton from '../SocialIconButton';

import { ImageContentProps, SocialContent } from './props';
import {
  ContentSkeletonWrapper,
  ContentWrapper,
  ImageHoverWrapper,
  ImageWrapper,
  SocialIconsWrapper,
  StyledDescription,
  StyledTitle,
  TitleSkeletonWrapper,
  Wrapper,
} from './styles';

export type keyType =
  | 'x'
  | 'youtube'
  | 'facebook'
  | 'instagram'
  | 'xiaohongshu'
  | 'tiktok'
  | 'linked-in';

export const ImageContent: React.FC<ImageContentProps> = (props) => {
  const {
    imagePosition = 'top',
    mobileImagePostion = 'top',
    tabletImagePosition = 'top',
    data,
    simpleMobileView = false,
    maxImageWidth,
    maxTabletImageWidth,
    loading,
    displayImage = true,
    imageProps,
    webView,
  } = props;

  const { clickEvent } = usePostMsgContext();
  const [maxDisplay, setMaxDisplay] = useState<number>(Math.floor(175 / 40));
  const { name, role, social, username, totalPost } = data || {};
  const href = webView ? `/webview/author/${username}` : `/author/${username}`;

  const hasImage = !!imageProps?.src;
  const flexDirection =
    imagePosition === 'right'
      ? 'row-reverse'
      : imagePosition === 'left'
      ? 'row'
      : 'column';
  const tabletDirection =
    tabletImagePosition === 'right'
      ? 'row-reverse'
      : tabletImagePosition === 'left'
      ? 'row'
      : 'column';
  const mobileDirection =
    mobileImagePostion === 'right'
      ? 'row-reverse'
      : mobileImagePostion === 'left'
      ? 'row'
      : 'column';
  const simpleMbView = simpleMobileView;

  const socialIconsWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (socialIconsWrapperRef.current) {
        setMaxDisplay(
          Math.floor(socialIconsWrapperRef?.current?.clientWidth / 40)
        );
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderSocialIconButtons = () => {
    return (
      <SocialIconsWrapper ref={socialIconsWrapperRef}>
        {social &&
          Object.keys(social)
            ?.filter((e) => !!social[e as keyType]?.user_social_url)
            ?.splice(0, maxDisplay)
            .map((key) => {
              const socialKey = key as keyType;
              const socialContent = social[socialKey] as SocialContent;
              return (
                socialContent?.user_social_url && (
                  <SocialIconButton
                    key={key}
                    isSvg
                    href={socialContent?.user_social_url}
                  >
                    {SocialIcons[socialKey]}
                  </SocialIconButton>
                )
              );
            })}
      </SocialIconsWrapper>
    );
  };

  if (loading) {
    return (
      <Wrapper
        $flexDirection={flexDirection}
        $mobileDirection={mobileDirection}
        $tabletDirection={tabletDirection}
      >
        <ImageWrapper
          $aspectRatio={(imageProps?.width ?? 1) / (imageProps?.height ?? 1)}
          $width={imagePosition !== 'left' ? `100%` : `${imageProps?.width}px`}
          $tabletWidth={tabletImagePosition === 'top' ? '100%' : '30%'}
          $mobileWidth={'100%'}
          $maxWidth={maxImageWidth ? maxImageWidth : undefined}
          $maxLgWidth={maxTabletImageWidth}
        >
          <div
            className={classNames('display-none', {
              'display-block skeleton skeleton-overlay': loading,
            })}
          />
        </ImageWrapper>
        <ContentSkeletonWrapper className="content">
          <ContentBody>
            <StyledTitle style={{ minWidth: '100%', margin: 0 }}>
              <TitleSkeletonWrapper>
                <div
                  className="skeleton skeleton-title-text"
                  style={{
                    minWidth: '100%',
                  }}
                />
              </TitleSkeletonWrapper>
            </StyledTitle>
            <StyledDescription>
              <div
                className="skeleton skeleton-description-text"
                style={{
                  minWidth: '50%',
                }}
              />
            </StyledDescription>
          </ContentBody>
        </ContentSkeletonWrapper>
      </Wrapper>
    );
  }

  // ======================= VIEWS
  return (
    <Wrapper
      $flexDirection={flexDirection}
      $mobileDirection={mobileDirection}
      $tabletDirection={tabletDirection}
    >
      {hasImage && displayImage && (
        <ImageWrapper
          $aspectRatio={(imageProps?.width ?? 1) / (imageProps?.height ?? 1)}
          $width={imagePosition !== 'left' ? `100%` : `${imageProps?.width}px`}
          $tabletWidth={tabletImagePosition === 'top' ? '100%' : '30%'}
          $mobileWidth={simpleMbView ? '60%' : '100%'}
          $maxWidth={maxImageWidth ? maxImageWidth : undefined}
          $maxLgWidth={
            tabletImagePosition === 'top' ? '100%' : maxTabletImageWidth
          }
        >
          <div
            className={classNames('display-none', {
              // 'display-block loader': loading,
            })}
          />
          <div
            className={classNames('display-none', {
              'display-block skeleton-overlay': loading,
            })}
          />

          <Link
            href={href}
            className={classNames('', {
              'display-none': loading,
            })}
            onClick={clickEvent(href)}
          >
            <ImageHoverWrapper>
              <StyledImage {...imageProps} />
            </ImageHoverWrapper>
          </Link>
        </ImageWrapper>
      )}
      <ContentWrapper>
        <div>
          <Link className={'url-ani-2'} href={href} onClick={clickEvent(href)}>
            <StyledTitle className="url-ani-2" $loading={loading}>
              {name}
            </StyledTitle>
          </Link>
          <StyledDescription>
            {role} {totalPost && ` / ${totalPost} Posts`}
          </StyledDescription>
        </div>
        {renderSocialIconButtons()}
      </ContentWrapper>
    </Wrapper>
  );
};

// ======================= EXPORT
export * from './props';
export default ImageContent;
